import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';

import {
  AppBar, Avatar, Box, Divider, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  MenuItem, Select, Tab, Tabs, Toolbar, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import logo from './logo.jpg';

import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

const BRAND = '#c3352a';
const BG = '#0f0707';

const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'de', label: 'DE', name: 'Deutsch' },
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'it', label: 'IT', name: 'Italiano' },
  { code: 'sv', label: 'SV', name: 'Svenska' },
  { code: 'es', label: 'ES', name: 'Español' },
];

const navLinks = [
  { key: 'home',      link: '/',          icon: <HomeFilledIcon fontSize="small" /> },
  { key: 'dashboard', link: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
  { key: 'settings',  link: '/settings',  icon: <SettingsIcon fontSize="small" /> },
  { key: 'images',    link: '/images',    icon: <PhotoLibraryIcon fontSize="small" /> },
  { key: 'logs',      link: '/logs',      icon: <ArticleIcon fontSize="small" /> },
];

function getInitials(user) {
  const id = user?.signInDetails?.loginId ?? user?.username ?? '';
  return id.split('@')[0].slice(0, 2).toUpperCase() || '?';
}

function Navbar({ signOut, user }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [company, setCompany] = React.useState(null);
  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    fetchAuthSession().then(session => {
      setCompany(session.tokens?.idToken?.payload?.['custom:company'] ?? null);
    });
  }, []);

  const activeTab = navLinks.find(({ link }) =>
    link === '/' ? location.pathname === '/' : location.pathname.startsWith(link)
  )?.link ?? false;

  const lang = i18n.language.slice(0, 2);

  return (
    <AppBar position="sticky" elevation={0} sx={{ backgroundColor: BG, borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}>
      <Toolbar disableGutters sx={{ px: { xs: 1.5, sm: 2 }, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
          <img src={logo} alt="GTMS" style={{ height: 30, display: 'block' }} />
        </Box>

        {isMobile ? (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ color: alpha('#fff', 0.8) }}
              aria-label="open navigation"
            >
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{ sx: { backgroundColor: '#150a0a', width: 230 } }}
            >
              <Box sx={{ pt: 2.5 }}>
                {user && (
                  <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      component={Link}
                      to="/settings/profile"
                      onClick={() => setDrawerOpen(false)}
                      sx={{ bgcolor: BRAND, width: 38, height: 38, fontSize: '0.8rem', textDecoration: 'none', cursor: 'pointer' }}
                    >
                      {getInitials(user)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.3, fontSize: '0.85rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {user.signInDetails?.loginId ?? user.username}
                      </Typography>
                      {company && (
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.45), display: 'block' }}>
                          {company}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

                <List dense sx={{ py: 1 }}>
                  {navLinks.map(({ key, link, icon }) => (
                    <ListItem key={key} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={link}
                        selected={activeTab === link}
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                          px: 2,
                          color: activeTab === link ? '#fff' : alpha('#fff', 0.6),
                          borderLeft: activeTab === link ? `3px solid ${BRAND}` : '3px solid transparent',
                          '&.Mui-selected': { backgroundColor: alpha(BRAND, 0.12) },
                          '&:hover': { backgroundColor: alpha('#fff', 0.06), color: '#fff' },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>{icon}</ListItemIcon>
                        <ListItemText
                          primary={t(`nav.${key}`)}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: activeTab === link ? 600 : 400 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

                <Box sx={{ px: 2, py: 1.5 }}>
                  <Select
                    value={lang}
                    onChange={(e) => { setDrawerOpen(false); i18n.changeLanguage(e.target.value); }}
                    size="small"
                    variant="standard"
                    disableUnderline
                    sx={{
                      color: alpha('#fff', 0.65),
                      fontSize: '0.875rem',
                      minWidth: 110,
                      '& .MuiSvgIcon-root': { color: alpha('#fff', 0.45) },
                    }}
                  >
                    {LANGUAGES.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>{name}</MenuItem>
                    ))}
                  </Select>
                </Box>

                <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

                <List dense sx={{ py: 1 }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => { setDrawerOpen(false); signOut(); }}
                      sx={{
                        px: 2,
                        color: alpha('#fff', 0.55),
                        '&:hover': { backgroundColor: alpha(BRAND, 0.15), color: BRAND },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}><LogoutIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            <Tabs
              value={activeTab}
              sx={{
                flexGrow: 1,
                minHeight: 56,
                '& .MuiTabs-indicator': { backgroundColor: BRAND, height: 2 },
              }}
            >
              {navLinks.map(({ key, link, icon }) => (
                <Tab
                  key={key}
                  value={link}
                  label={t(`nav.${key}`)}
                  icon={icon}
                  iconPosition="start"
                  component={Link}
                  to={link}
                  sx={{
                    minHeight: 56,
                    px: 1.5,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: alpha('#fff', 0.6),
                    '&.Mui-selected': { color: '#fff', fontWeight: 600 },
                    '&:hover': { color: '#fff', backgroundColor: alpha('#fff', 0.05) },
                  }}
                />
              ))}
            </Tabs>

            {user && (
              <Tooltip
                title={`${user.signInDetails?.loginId ?? user.username}${company ? ` · ${company}` : ''}`}
                placement="bottom"
              >
                <Box
                  component={Link}
                  to="/settings/profile"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mr: 1.5,
                    textDecoration: 'none', borderRadius: 1, px: 0.75, py: 0.5,
                    '&:hover': { backgroundColor: alpha('#fff', 0.07) },
                  }}
                >
                  <Avatar sx={{ bgcolor: BRAND, width: 28, height: 28, fontSize: '0.7rem' }}>
                    {getInitials(user)}
                  </Avatar>
                  <Box sx={{ lineHeight: 1 }}>
                    <Typography sx={{ color: alpha('#fff', 0.75), fontSize: '0.8rem', lineHeight: 1.2 }}>
                      {(user.signInDetails?.loginId ?? user.username).split('@')[0]}
                    </Typography>
                    {company && (
                      <Typography sx={{ color: alpha('#fff', 0.38), fontSize: '0.68rem', lineHeight: 1.2, display: 'block' }}>
                        {company}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Tooltip>
            )}

            <Select
              value={lang}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                color: alpha('#fff', 0.55),
                mr: 0.5,
                fontSize: '0.8rem',
                '& .MuiSelect-select': { py: 0, pr: '20px !important' },
                '& .MuiSvgIcon-root': { color: alpha('#fff', 0.38) },
              }}
            >
              {LANGUAGES.map(({ code, label }) => (
                <MenuItem key={code} value={code}>{label}</MenuItem>
              ))}
            </Select>

            <Tooltip title={t('nav.logout')}>
              <IconButton
                onClick={signOut}
                size="small"
                sx={{
                  ml: 0.5,
                  color: alpha('#fff', 0.5),
                  '&:hover': { backgroundColor: alpha(BRAND, 0.15), color: BRAND },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
